// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Container } from "@hardwoods/App/Components/Container/Container";
import systemColors from "@hardwoods/XDL/PreStyle/colors";
import { color } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { withCategory } from "@insite/client-framework/Components/CategoryContext";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClickablePresentationProps } from "@insite/mobius/Clickable/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    borderColor = "borderColor",
    imagePosition = "imagePosition",
    backgroundType = "backgroundType",
    categoryImagePath = "categoryImagePath",
    backgroundColor = "backgroundColor",
}

interface Props extends WidgetProps {
    fields: {
        [fields.imagePosition]: string;
        [fields.borderColor]: string;
        [fields.imagePosition]: string;
        [fields.categoryImagePath]: "both" | "largePath" | "smallPath";
        [fields.backgroundType]: "image" | "color";
        [fields.backgroundColor]: string;
    };
    category?: Category;
}

export interface CategoryDetailBannerStyles {
    wrappingBannerImage?: ClickablePresentationProps;
}

export const customBannerStyles: CategoryDetailBannerStyles = {
    wrappingBannerImage: {
        css: css`
            display: flex;
            justify-content: center;

            height: 140px;
            border-bottom: 10px solid transparent;
            ${({ borderColor }: any) =>
                borderColor &&
                css`
                    border-bottom-color: ${borderColor};
                `}

            ${({ backgroundType, backgroundImage, imagePosition, backgroundColor }: any) =>
                backgroundType === "image"
                    ? css`
                          background-image: ${backgroundImage};
                          background-position: ${imagePosition};
                          background-repeat: no-repeat;
                          background-size: cover;
                          &.hide {
                              visibility: hidden;
                              height: 0;
                          }
                      `
                    : css`
                          background-color: ${backgroundColor};
                      `}
        `,
    },
};

const styles = customBannerStyles;

const CategoryImage = ({ category, fields }: Props) => {
    if (!category) {
        return null;
    }

    const { imagePosition, backgroundColor, backgroundType, borderColor, categoryImagePath } = fields;
    let imagePath;
    switch (categoryImagePath) {
        case "largePath":
            imagePath = category?.largeImagePath;
            break;
        case "smallPath":
            imagePath = category?.smallImagePath;
            break;
        default:
            imagePath = `${category?.largeImagePath || category?.smallImagePath}`;
            break;
    }

    const isHidden = (imagePath === undefined);
    const bannerImageProps = {
        backgroundImage: `url(${imagePath})`,
        imagePosition,
        backgroundColor,
        backgroundType: backgroundType || "image",
        borderColor,
    };

    return (
        <StyledWrapper
            {...styles.wrappingBannerImage}
            {...bannerImageProps}
            data-test-selector="categoryDetailBanner-bannerLink"
            className={isHidden ? "hide" : ""}
        />
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryImage),
    definition: {
        group: "Category Details",
        icon: "image",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.backgroundType,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                fieldType: "General",
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                name: fields.categoryImagePath,
                displayName: "Choose Category Image Path",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Both", value: "both" },
                    { displayName: "Large Path", value: "largePath" },
                    { displayName: "Small Path", value: "smallPath" },
                ],
                fieldType: "General",
                defaultValue: "both",
                isVisible: widget => widget.fields.backgroundType === "image",
                hideEmptyOption: true,
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                fieldType: "General",
                isVisible: widget => widget.fields.backgroundType === "color",
            },
            {
                fieldType: "General",
                name: fields.imagePosition,
                displayName: "Image position",
                editorTemplate: "TextField",
                defaultValue: "left center",
                isRequired: false,
                isVisible: widget => widget.fields.backgroundType === "image",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("background-position", value) ? null : `Invalid Input`;
                },
            },
            {
                fieldType: "General",
                name: fields.borderColor,
                displayName: "Border color",
                editorTemplate: "ColorPickerField",
                isRequired: false,
                defaultValue: (systemColors.common as any).secondary2,
            },
        ],
    },
};

export default widgetModule;
